import React from "react";

const base = "w-full px-4 py-3 rounded-2xl bg-white dark:bg-emerald-900/60 border text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 transition";
const border = (error) => (error ? "border-rose-400" : "border-slate-200 dark:border-emerald-800");

export const Label = ({ children, required }) => (
  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ml-1">
    {children} {required && <span className="text-rose-500">*</span>}
  </label>
);

const Error = ({ error, testid }) => error ? <p data-testid={`${testid}-error`} className="text-[11px] text-rose-600 mt-1 ml-1">{error}</p> : null;

export const Field = ({ label, testid, name, required, error, hint, onHint, ...props }) => (
  <div data-field={name}>
    <div className="flex items-center justify-between">
      <Label required={required}>{label}</Label>
      {hint && <button type="button" onClick={onHint} className="text-[11px] font-semibold text-emerald-600 mb-1.5">{hint}</button>}
    </div>
    <input data-testid={testid} {...props} className={`${base} ${border(error)}`} />
    <Error error={error} testid={testid} />
  </div>
);

export const TextArea = ({ label, testid, name, required, error, rows = 2, ...props }) => (
  <div data-field={name}>
    <Label required={required}>{label}</Label>
    <textarea data-testid={testid} rows={rows} {...props} className={`${base} ${border(error)} resize-none`} />
    <Error error={error} testid={testid} />
  </div>
);

export const Select = ({ label, testid, name, options, placeholder, required, error, ...props }) => (
  <div data-field={name}>
    <Label required={required}>{label}</Label>
    <select data-testid={testid} {...props} className={`${base} ${border(error)} appearance-none`}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
    <Error error={error} testid={testid} />
  </div>
);

export const Chips = ({ label, testid, options, value, onChange }) => {
  const toggle = (o) => {
    if (o === "None") return onChange(value.includes("None") ? [] : ["None"]);
    const next = value.filter((x) => x !== "None");
    onChange(next.includes(o) ? next.filter((x) => x !== o) : [...next, o]);
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => {
          const active = value.includes(o);
          return (
            <button
              type="button"
              key={o}
              data-testid={`${testid}-${o.replace(/[^a-z]/gi, "-").toLowerCase()}`}
              onClick={() => toggle(o)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                active ? "bg-emerald-700 text-white border-emerald-700" : "bg-white dark:bg-emerald-900/60 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-emerald-800"
              }`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const Section = ({ children }) => (
  <div className="p-4 rounded-3xl bg-white/70 dark:bg-emerald-900/30 border border-slate-100 dark:border-emerald-800 shadow-sm space-y-3.5">{children}</div>
);

export const SectionTitle = ({ icon: Icon, step, title, required }) => (
  <div className="flex items-center gap-3 pb-1">
    <div className="w-9 h-9 rounded-xl bg-emerald-800 dark:bg-emerald-600 text-white flex items-center justify-center shrink-0">
      <Icon className="w-4.5 h-4.5" />
    </div>
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#b8964e]">Section {step}</p>
      <h2 className="text-base font-extrabold text-emerald-950 dark:text-emerald-50 font-head leading-tight">{title} {required && <span className="text-rose-500">*</span>}</h2>
    </div>
  </div>
);
